/**
 * Stats Domain Module
 *
 * Handles all statistics-related operations including:
 * - Daily stats queries
 * - Monthly stats aggregation
 * - Product stats queries
 *
 * This module provides proper TypeScript types for stats operations.
 */

import { format, parse } from 'date-fns'
import { getDefaultStore } from 'jotai'
import * as Sentry from '@sentry/react-native'

// Note: rootDb import removed - not needed in this module

// Shared types and utilities
import {
  shouldUseSupabase,
  type DateRange,
  type TimestampLike,
  type MockRef,
} from './types'

import { dbAtom, organizationAtom } from '../AtomsProvider'

import { reportsApi } from '../API/typedApiClient'
import { getLocalStoreDate, getStoreBusinessDate, dailyStatIDForBusinessDay, businessDayAsLocalDate, businessDateRangeStrings } from '../../utils/timezoneFunctions'
import { validateDailyStatsId } from '../../utils/validationFunctions'

// ============================================================================
// Types
// ============================================================================

/**
 * Daily statistics record
 */
export interface DailyStat {
  id?: string
  ref?: MockRef
  date: Date | TimestampLike
  store: string
  organizationId: string

  // Sales metrics
  salesTotal?: number
  salesCount?: number
  salesCash?: number
  salesCard?: number
  salesQR?: number
  returnsTotal?: number
  returnsCount?: number

  // Inventory metrics
  suppliesTotal?: number
  suppliesCount?: number
  writeOffsTotal?: number
  writeOffsCount?: number
  transfersCount?: number

  // Client metrics
  newBonusClients?: number
  bonusUsed?: number
  bonusAdded?: number

  // Product metrics
  productsCreated?: number
  productsDeleted?: number

  // Other metrics
  spendingsTotal?: number
  cashboxTransactionsTotal?: number

  [key: string]: unknown
}

/**
 * Monthly statistics record
 */
export interface MonthlyStat {
  id?: string
  ref?: MockRef
  date: Date | TimestampLike
  store: string
  organizationId: string
  [key: string]: unknown
}

/**
 * One per-date product report doc, the legacy Firestore `dailyProductStats` `*-productReport-*`
 * shape: `{ [productKey]: { name, saleQuantity, saleAmount, writeOffQuantity, … }, date, store }`.
 * Product keys are dynamic, so they live in the index signature. (ATL-1329: this used to declare
 * `soldQuantity` / `purchasedQuantity` / … — fields neither store's doc has.)
 */
export interface ProductStat {
  id?: string
  ref?: MockRef
  date: Date | TimestampLike
  store: string | null

  [key: string]: unknown
}

/**
 * Stats grouped by date (dictionary format)
 */
export type StatsByDate<T> = Record<string, T>

/**
 * Fields a stats bucket carries through VERBATIM instead of summing.
 *
 * The dict/roll-up loops below add every OTHER key of a row into the bucket, and `+` on a string
 * concatenates rather than adds: the Supabase read path's `storeName`, `timezone` and
 * `businessDate` came back as '0Сыра market', '0Asia/Almaty' and '02026-09-02' — values no
 * consumer can use and every consumer would believe. One list for all five loops (daily and
 * monthly, both read paths) so a field cannot be excluded in one and summed in another.
 *
 * The list must name EVERY non-numeric key `/reports/daily-stats` can carry, not just the three
 * that were noticed: the generated response schema is `.passthrough()`, so every field
 * `transformDailyStat` emits reaches these loops. `storeId` (the very key ATL-1116 is about) and
 * the two shift timestamps are strings, and the two payment-method breakdowns are objects — left
 * out, a bucket's `storeId` reads '0GQEz3SxzfKZHnN48aJ0k', `shiftOpenedTime` reads
 * '02026-09-02T05:00:00+00:00' and `paymentMethodAmounts` reads '0[object Object]'. `firestoreId`
 * and `supabaseId` are always null on this response and would otherwise be asserted as 0.
 *
 * `shiftOpened` / `shiftClosed` are the LAST two, and the easiest to miss because a boolean does
 * not look like a string: `0 + true` is 1 and `1 + true` is 2, so summing them silently converts
 * "the shift was opened" into a COUNT of the rows that said so — a day/store bucket reads
 * `shiftOpened: 3` for a three-store fan-out, and `ProfitStats`' `value.shiftOpened == null`
 * probe (its only test for the legacy stat shape) is answered by a number that was never in the
 * data. Carried through verbatim like every other non-numeric field.
 */
const NON_NUMERIC_STAT_FIELDS = new Set([
  'date',
  'businessDate',
  'id',
  'ref',
  'store',
  'storeId',
  'storeName',
  'firestoreId',
  'supabaseId',
  'timezone',
  'organizationId',
  'shiftOpenedTime',
  'shiftClosedTime',
  'paymentMethodAmounts',
  'paymentMethodQuantities',
  'shiftOpened',
  'shiftClosed',
])

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get Jotai store and database reference
 */
const getDbContext = () => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organization = jotaiStore.get(organizationAtom)
  return {
    jotaiStore,
    db,
    organization,
    organizationId: (organization as { id?: string })?.id,
  }
}

// ============================================================================
// Daily Stats Functions
// ============================================================================

/**
 * Get daily statistics for a date range.
 *
 * PRECONDITION (ATL-1112): in Supabase mode `range` must be a range produced by
 * `dateRangeWithTimezoneAndAutoclose`, and `store` must name the SAME store set it was built
 * from. The bounds are decoded by inverting that encoding, and an inverse applied to a range
 * that was never encoded subtracts an autoclose shift that is not there and reads the edge in a
 * frame that never framed it — either of which can move a bound by a day. Every caller now
 * encodes first; `screens/Admin/MiscScreen.js` was the last that did not.
 */
export const getStatsForDays = async (
  db: unknown,
  range: DateRange,
  store: string,
  dict = false
): Promise<DailyStat[] | StatsByDate<DailyStat>> => {
  if (shouldUseSupabase()) {
    try {
      // ATL-1112: `/reports/daily-stats` filters `business_date`, a DATE column, so each bound
      // must be the store's BUSINESS DAY — not the instant that begins it. Sending
      // `toISOString()` let Postgres cast the instant to a date and discard the offset, which
      // pulled the start bound onto the previous day for every UTC+ store: a single day's P&L
      // summed two days, and a FUTURE day returned today's takings instead of nothing.
      const bounds = businessDateRangeStrings(range, store ? [store] : null)
      // Refuse an unbounded read rather than performing one. Before ATL-1112 a missing bound was
      // sent as the literal string "undefined", which Postgres rejected — a loud 400. Omitting
      // the param instead makes `get_daily_stats` treat the bound as NULL, i.e. UNBOUNDED, so the
      // caller would silently receive the organization's ENTIRE history and report it as the
      // range it asked for. In accounting software a wrong total that looks right is the worse
      // failure, so keep it loud.
      if (bounds.startDate == null || bounds.endDate == null) {
        throw new Error(
          'getStatsForDays: both range bounds are required — an absent bound would fetch every ' +
            'daily stat the organization has and present it as the requested range.'
        )
      }
      const params: Record<string, string> = {
        startDate: bounds.startDate,
        endDate: bounds.endDate,
      }

      if (store) {
        params.storeId = store
      }

      // Typed client returns DailyStatsResponse[] with `date` already converted to a
      // TimestampLike object (same as the old transformApiItem path), validated via Zod.
      const stats = (await reportsApi.getDailyStats(params) as unknown as DailyStat[]).map(stat => {
        const raw = stat as Record<string, unknown>
        // Normalize store field from store_id or storeId to store
        if (!stat.store && (raw.store_id || raw.storeId)) {
          stat.store = (raw.store_id || raw.storeId) as string
        }
        // Generate ID if missing (daily_stats view doesn't have id field).
        //
        // Built from the row's business day with the autoclose-NAIVE spelling, NOT with
        // `getDailyStatID`. `get_daily_stats` groups on `sales.business_date` and on
        // `business_date_for(ts, tz, cutoff)`, so the timezone and the autoclose cutoff have
        // already been applied server-side; `getDailyStatID` applies them a SECOND time to what
        // reaches the app as UTC midnight of that day, and books the row onto D-1 for every store
        // whose cutoff exceeds its UTC offset and for every store west of Greenwich. That was
        // harmless only while ATL-1118 had `store` carrying the Supabase UUID — an id that could
        // not hit any document anyway. With the Firestore id it hits the PREVIOUS day's
        // `productStockSnapshots/<d-M-y-store>-stock`, and the P&L prices its opening stock off
        // the wrong day without saying so. `businessDate` is the raw `YYYY-MM-DD` (it is not in
        // the api-client's date-field list, so it is never re-framed); `date` is the same day as
        // a UTC-midnight instant and is read back in UTC by the helper.
        if (!stat.id && stat.store && stat.date) {
          stat.id =
            dailyStatIDForBusinessDay(
              (raw.businessDate as string | undefined) ?? (stat.date as TimestampLike | Date),
              stat.store
            ) ?? undefined
        }
        // Flatten the Supabase nested payment-method breakdown into the flat
        // `<methodId>SalesAmount` / `<methodId>SalesQuantity` keys the Sales report
        // screen reads (Firestore dailyStats docs already carry these flat keys).
        // The view aggregates jsonb keyed as `<methodId>_sales_amount`, so strip
        // that suffix to recover the method id and rebuild the screen's key. (ATL-611)
        //
        // ATL-1304 — this FILLS A GAP; it must never overwrite. The backend now also sends
        // these keys directly, merged from `get_shift_report`, and that version is the one the
        // Firestore writer would have written: `dailyStatIncrementsForSales` books
        // `pm.value || finalTotalCartPrice` for the primary method AND `pm2.value` for the
        // second, whereas `paymentMethodAmounts` is `sum(total_price)` grouped by the PRIMARY
        // method alone. On a split-payment sale the nested jsonb hands the whole cart to the
        // first method and the second none, which is what understated «Комиссия банка» on four
        // production organizations. An unconditional assignment here would silently restore
        // that number after the fix, and no consumer could tell the two apart.
        //
        // The test is `in`, not a falsy check: a backend `0` is a real figure ("nobody paid by
        // QR today"), and `stat[key] || flattened` would read it as absent and replace it —
        // the `|| fallback` that masks good data with worse data. Decided per key, because the
        // shift report only names a method that traded while the jsonb can name another.
        const assignIfAbsent = (key: string, value: number): void => {
          if (key in (stat as Record<string, unknown>)) return
          ;(stat as Record<string, unknown>)[key] = value
        }
        const pmAmounts = raw.paymentMethodAmounts as Record<string, unknown> | undefined
        if (pmAmounts) {
          for (const k in pmAmounts) {
            if (k.endsWith('_sales_amount')) {
              const id = k.slice(0, -'_sales_amount'.length)
              assignIfAbsent(id + 'SalesAmount', Number(pmAmounts[k]) || 0)
            }
          }
        }
        const pmQuantities = raw.paymentMethodQuantities as Record<string, unknown> | undefined
        if (pmQuantities) {
          for (const k in pmQuantities) {
            if (k.endsWith('_sales_quantity')) {
              const id = k.slice(0, -'_sales_quantity'.length)
              assignIfAbsent(id + 'SalesQuantity', Number(pmQuantities[k]) || 0)
            }
          }
        }
        // No `validateDailyStatsId` here, deliberately. That function asks whether a STORED
        // document's id agrees with its own `date`, and repairs the date through `stat.ref`. On
        // this path there is no stored id (the view has none — the id two blocks up was just
        // synthesized from this very row's business day) and no usable `ref` (the backend emits
        // `ref: { id: undefined }`), so it can only ever compare a value with itself and then warn
        // that it cannot write. The Firestore branch below, which has both, still runs it.
        return stat
      })

      if (dict) {
        const dSbyDate: StatsByDate<DailyStat> = {}
        for (const value of stats) {
          const statDate = value.date && typeof (value.date as TimestampLike).toDate === 'function'
            ? (value.date as TimestampLike).toDate()
            : new Date(value.date as string | Date)
          // ATL-1116: bucket on the day the ROW names, not on a second timezone conversion of it.
          // The consumers build their lookup key from a real range instant
          // (`format(getLocalStoreDate(store, <range day>), 'd/M/y E')` in `SalesStats` and in
          // `compareProductReportAndDailyStats`), i.e. the store's calendar day. Deriving the key
          // here by rendering the row's date-only value at the store's wall clock agrees with that
          // only for a store EAST of UTC: UTC midnight rendered in a UTC-4 zone is the PREVIOUS
          // day, so every key would miss and the Sales report would read zeros for a range that
          // has data. `businessDate` is the row's own `YYYY-MM-DD`.
          //
          // A response that predates `businessDate` falls back to `date`, read the SAME way the id
          // synthesis above reads it — in UTC. On this branch `date` IS the business day (the
          // backend emits the one `daily_stats.date` value through both fields, and a `date`
          // column reaches the app as UTC midnight), so `getLocalStoreDate` would re-apply the
          // store offset to a value that has already had it applied — the very double conversion
          // this block removes, and it would leave the bucket key naming a different day than the
          // id built from the same row. It stays only as the guard for a `date` that names no day
          // at all.
          const businessDay =
            businessDayAsLocalDate((value as Record<string, unknown>).businessDate as string | undefined) ??
            businessDayAsLocalDate(statDate)
          const dateKey = format(businessDay ?? getLocalStoreDate(store, statDate), 'd/M/y E')
          dSbyDate[dateKey] = dSbyDate[dateKey] || {} as DailyStat
          for (const key in value) {
            if (!NON_NUMERIC_STAT_FIELDS.has(key)) {
              (dSbyDate[dateKey] as Record<string, unknown>)[key] = ((dSbyDate[dateKey] as Record<string, unknown>)?.[key] as number || 0) + ((value as Record<string, unknown>)[key] as number || 0)
            } else {
              (dSbyDate[dateKey] as Record<string, unknown>)[key] = (value as Record<string, unknown>)[key]
            }
          }
        }
        return dSbyDate
      }
      return stats
    } catch (error) {
      console.error('Error fetching daily stats from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'dailyStats', function: 'getStatsForDays' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  type FirestoreQuery = {
    where: (field: string, op: string, value: unknown) => FirestoreQuery
    orderBy: (field: string, direction: string) => FirestoreQuery
    get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => DailyStat }) => void) => void }>
  }

  let querySnapshot: FirestoreQuery = (db as { collection: (name: string) => FirestoreQuery }).collection('dailyStats')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  const result = await querySnapshot
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'asc')
    .get()

  const docs: DailyStat[] = []
  result.forEach((doc: { id: string; ref: MockRef; data: () => DailyStat }) => {
    const snap = doc.data()
    snap.id = doc.id
    snap.ref = doc.ref
    void validateDailyStatsId(snap, true)
    docs.push(snap)
  })

  if (dict) {
    const dSbyDate: StatsByDate<DailyStat> = {}
    for (const value of docs) {
      // Bucket on the store's business day (autoclose-aware), matching how the sale
      // was assigned to this dailyStats doc (getDailyStatID). The Firestore `date`
      // field is a mutation-instant timestamp, so for a UTC+ store post-midnight
      // (pre-cutoff) activity lands on the next calendar day under the autoclose-naive
      // getLocalStoreDate, zeroing the real day in the Sales report (ATL-674).
      const dateKey = format(getStoreBusinessDate(store, (value.date as TimestampLike).toDate()), 'd/M/y E')
      dSbyDate[dateKey] = dSbyDate[dateKey] || {} as DailyStat
      for (const key in value) {
        if (!NON_NUMERIC_STAT_FIELDS.has(key)) {
          (dSbyDate[dateKey] as Record<string, unknown>)[key] = ((dSbyDate[dateKey] as Record<string, unknown>)?.[key] as number || 0) + ((value as Record<string, unknown>)[key] as number || 0)
        } else {
          (dSbyDate[dateKey] as Record<string, unknown>)[key] = (value as Record<string, unknown>)[key]
        }
      }
    }
    return dSbyDate
  }
  return docs
}

// ============================================================================
// Monthly Stats Functions
// ============================================================================

/**
 * Get monthly statistics for a date range
 */
export const getMonthlyStatsOnDateRange = async (
  range: DateRange | null,
  store: string,
  dict = false
): Promise<MonthlyStat[] | StatsByDate<MonthlyStat>> => {
  if (shouldUseSupabase()) {
    try {
      // ATL-1112: same endpoint, same DATE-typed bound — see getStatsForDays above.
      const bounds = businessDateRangeStrings(range ?? {}, store ? [store] : null)
      const params: Record<string, string> = {}

      if (bounds.startDate != null) {
        params.startDate = bounds.startDate
      }
      if (bounds.endDate != null) {
        params.endDate = bounds.endDate
      }

      if (store) {
        params.storeId = store
      }

      // Typed client; `date` arrives as a TimestampLike object (validated via Zod).
      const stats = await reportsApi.getDailyStats(params) as unknown as MonthlyStat[]

      // Aggregate by month
      const monthlyStats: Record<string, MonthlyStat> = {}
      stats.forEach(stat => {
        const statDate = stat.date && typeof (stat.date as TimestampLike).toDate === 'function'
          ? (stat.date as TimestampLike).toDate()
          : new Date(stat.date as string | Date)
        // ATL-1116: the month a row belongs to is the month its BUSINESS DAY names — not the month
        // a second timezone conversion of it lands in. `date` is a date-only `YYYY-MM-DD` the
        // api-client deserialises to UTC midnight, and reading its DEVICE-local calendar fields
        // moves it back a day on any device west of UTC: the 1st of every month would be summed
        // into the previous month. Same helper, same reason, and the same UTC-read fallback for a
        // response that predates `businessDate`, as the daily bucket above.
        const monthDay =
          businessDayAsLocalDate((stat as unknown as Record<string, unknown>).businessDate as string | undefined) ??
          businessDayAsLocalDate(statDate) ??
          statDate
        const monthKey = format(monthDay, 'M/y')
        if (!monthlyStats[monthKey]) {
          monthlyStats[monthKey] = {
            date: new Date(monthDay.getFullYear(), monthDay.getMonth(), 1),
            store: (stat.store || (stat as Record<string, unknown>).storeId) as string,
            organizationId: stat.organizationId as string,
          }
        }
        for (const key in stat) {
          if (!NON_NUMERIC_STAT_FIELDS.has(key)) {
            (monthlyStats[monthKey] as Record<string, unknown>)[key] = ((monthlyStats[monthKey] as Record<string, unknown>)[key] as number || 0) + ((stat as Record<string, unknown>)[key] as number || 0)
          }
        }
      })

      const docs = Object.values(monthlyStats)

      if (dict) {
        const dSbyDate: StatsByDate<MonthlyStat> = {}
        for (const value of docs) {
          // ATL-1116: `value.date` was built three lines up as a DEVICE-local month start from a
          // business day that has already been resolved. Running it back through
          // `getLocalStoreDate` is a second conversion of a value that needs none, and it moves
          // the month for any store west of the device — the Firestore branch below is the one
          // that legitimately converts, because there `date` is a mutation instant.
          const dateKey = format(value.date as Date, 'M/y')
          dSbyDate[dateKey] = dSbyDate[dateKey] || {} as MonthlyStat
          for (const key in value) {
            if (!NON_NUMERIC_STAT_FIELDS.has(key)) {
              (dSbyDate[dateKey] as Record<string, unknown>)[key] = ((dSbyDate[dateKey] as Record<string, unknown>)?.[key] as number || 0) + ((value as Record<string, unknown>)[key] as number || 0)
            } else {
              (dSbyDate[dateKey] as Record<string, unknown>)[key] = (value as Record<string, unknown>)[key]
            }
          }
        }
        return dSbyDate
      }
      return docs
    } catch (error) {
      console.error('Error fetching monthly stats from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'monthlyStats', function: 'getMonthlyStatsOnDateRange' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const { db } = getDbContext()

  type FirestoreQuery = {
    where: (field: string, op: string, value: unknown) => FirestoreQuery
    orderBy: (field: string, direction: string) => FirestoreQuery
    get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => MonthlyStat }) => void) => void }>
  }

  let querySnapshot: FirestoreQuery = (db as { collection: (name: string) => FirestoreQuery }).collection('monthlyStats')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  if (range) {
    querySnapshot = querySnapshot.where('date', '>=', range.startDate)
    querySnapshot = querySnapshot.where('date', '<=', range.endDate)
  }
  const result = await querySnapshot
    .orderBy('date', 'asc')
    .get()

  const docs: MonthlyStat[] = []
  result.forEach((doc: { id: string; ref: MockRef; data: () => MonthlyStat }) => {
    const snap = doc.data()
    snap.id = doc.id
    snap.ref = doc.ref
    docs.push(snap)
  })

  if (dict) {
    const dSbyDate: StatsByDate<MonthlyStat> = {}
    for (const value of docs) {
      // Firestore monthlyStats `date` is a mutation-instant (info.date) while the doc is
      // assigned to its business month via getMonthlyStatDocID (autoclose-aware). Bucket on
      // the business month so a post-midnight (pre-cutoff) instant on the 1st isn't counted
      // in the next month (same class as ATL-674; getStatsForDays daily fix).
      const dateKey = format(getStoreBusinessDate(store, (value.date as TimestampLike).toDate()), 'M/y')
      dSbyDate[dateKey] = dSbyDate[dateKey] || {} as MonthlyStat
      for (const key in value) {
        if (!NON_NUMERIC_STAT_FIELDS.has(key)) {
          (dSbyDate[dateKey] as Record<string, unknown>)[key] = ((dSbyDate[dateKey] as Record<string, unknown>)?.[key] as number || 0) + ((value as Record<string, unknown>)[key] as number || 0)
        } else {
          (dSbyDate[dateKey] as Record<string, unknown>)[key] = (value as Record<string, unknown>)[key]
        }
      }
    }
    return dSbyDate
  }
  return docs
}

// ============================================================================
// Product Stats Functions
// ============================================================================

/**
 * Fetch the per-date product report docs from Supabase — the SAME legacy flat shape the Firestore
 * `dailyProductStats` `*-productReport-*` docs have: `{ [productKey]: entry, date, store }`.
 *
 * ATL-1329: `getProductStatsForDays` used to call `/reports/product-stats` here, which answers a
 * different question in a different shape (`{ [productId]: { [date]: { sold, purchased, … } } }`,
 * from movements). Every caller reads the legacy doc — `ProductStocksScreen` and
 * `SupplyOrderDetailScreen` loop `for (productID in doc)` and keep entries with a `name`, i.e.
 * `doc[productId].saleQuantity` — so on an org reading Supabase they found no entry at all and
 * the weekly sales/write-offs behind every suggested order quantity were silently 0.
 * `/reports/product-report` (ATL-566) already builds the legacy doc from source rows; both readers
 * now share this one fetch so the two cannot drift apart again.
 */
const fetchProductReportDocsFromSupabase = async (
  range: DateRange,
  store: string | null,
  collection: string
): Promise<Array<Record<string, unknown>>> => {
  try {
    const { organizationId } = getDbContext()

    if (!organizationId) {
      throw new Error('Organization ID not found')
    }

    // ATL-1112: these bounds stay INSTANTS on purpose. The rule is not "never send an instant",
    // it is "match the type of the column the bound lands on" — this endpoint filters
    // `sale_date` / `writeoff_date` / `supply_date`, which are `timestamptz`, not the `date`-typed
    // `sales.business_date` the daily-stats endpoint filters. Do not convert these by analogy.
    const params: { startDate: string; endDate: string; storeId?: string } = {
      startDate: range.startDate instanceof Date ? range.startDate.toISOString() : String(range.startDate),
      endDate: range.endDate instanceof Date ? range.endDate.toISOString() : String(range.endDate),
    }

    if (store) {
      params.storeId = store
    }

    return await reportsApi.getProductReport(params)
  } catch (error) {
    console.error(`Error fetching product report docs from Supabase (${collection}):`, error)
    Sentry.captureException(error, {
      tags: { supabase_fetch_error: true, collection },
      extra: {
        range,
        store,
        errorMessage: (error as Error).message,
      },
    })
    throw error
  }
}

/**
 * The dict key for a Supabase product report doc.
 *
 * ⚠️ These docs are bucketed by UTC day, not by the store's business day (ATL-566), so a dict built
 * from them does not line up day-for-day with `dailyStats`. Its one caller,
 * `compareProductReportAndDailyStats`, refuses in Supabase-read mode for that reason.
 *
 * The endpoint labels each doc with the UTC calendar date it aggregated (`YYYY-MM-DD`), which the
 * API client turns into a TimestampLike at UTC midnight. Formatting that instant in the store's
 * timezone — what the Firestore branch does with its own `date` — would move the label to the
 * previous day for any UTC− store, so the key is read from the UTC calendar fields instead: the doc
 * keeps the day the backend put it on.
 */
const productReportDocDateKey = (doc: Record<string, unknown>): string | null => {
  const raw = doc.date
  const instant =
    raw && typeof (raw as TimestampLike).toDate === 'function'
      ? (raw as TimestampLike).toDate()
      : typeof raw === 'string'
        ? new Date(raw)
        : null
  if (!instant || Number.isNaN(instant.getTime())) return null
  return format(new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate()), 'd/M/y E')
}

/**
 * Get the per-date product report docs for a date range (legacy flat shape, see
 * `fetchProductReportDocsFromSupabase`), as an array or keyed by `'d/M/y E'`.
 */
export const getProductStatsForDays = async (
  db: unknown,
  range: DateRange,
  store: string,
  dict = false,
  generateForMissingDays = false
): Promise<ProductStat[] | StatsByDate<ProductStat>> => {
  if (shouldUseSupabase()) {
    // Every day is aggregated from source rows server-side, so there is no "missing day" to
    // generate: `generateForMissingDays` only applies to the Firestore docs below.
    const docs = (await fetchProductReportDocsFromSupabase(range, store, 'productStats')) as ProductStat[]
    if (!dict) return docs

    const byDate: StatsByDate<ProductStat> = {}
    for (const doc of docs) {
      const key = productReportDocDateKey(doc)
      if (key) byDate[key] = doc
    }
    return byDate
  }

  // Firestore implementation
  type FirestoreQuery = {
    where: (field: string, op: string, value: unknown) => FirestoreQuery
    orderBy: (field: string, direction: string) => FirestoreQuery
    get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => ProductStat }) => void) => void }>
  }

  let querySnapshot: FirestoreQuery = (db as { collection: (name: string) => FirestoreQuery }).collection('dailyProductStats')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  const result = await querySnapshot
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'asc')
    .get()

  const docs: ProductStat[] = []
  result.forEach((doc: { id: string; ref: MockRef; data: () => ProductStat }) => {
    const snap = doc.data()
    snap.id = doc.id
    snap.ref = doc.ref
    docs.push(snap)
  })

  if (generateForMissingDays) {
    // Import required function for generating reports for missing dates
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { generateProductReportForDate } = require('../../utils/validationFunctions')

    // Check which dates are missing and need generating
    const missingDates: Record<string, boolean> = {}
    let date = new Date(range.startDate)
    while (date <= range.endDate) {
      missingDates[format(date, 'd-M-y')] = true
      date = new Date(date.setDate(date.getDate() + 1))
    }

    const todayDate = format(new Date(), 'd-M-y')
    for (const stat of docs) {
      const statDate = format((stat.date as TimestampLike).toDate(), 'd-M-y')
      if (statDate !== todayDate) {
        missingDates[statDate] = false
      } else {
        // remove today stat if previously generated so that it's not doubled
        docs.splice(docs.indexOf(stat), 1)
      }
    }

    for (const key in missingDates) {
      if (missingDates[key]) {
        const missingDate = parse(key, 'd-M-y', new Date())
        const missingDateStat = await generateProductReportForDate(missingDate, store)
        docs.push(missingDateStat)
      }
    }
  }

  if (dict) {
    const dSbyDate: StatsByDate<ProductStat> = {}
    for (const value of docs) {
      const dateKey = format(getLocalStoreDate(store, (value.date as TimestampLike).toDate()), 'd/M/y E')
      dSbyDate[dateKey] = value
    }
    return dSbyDate
  }
  return docs
}

/**
 * Get the per-date "product report" docs for a date range (ProductStats screen).
 *
 * Each doc is the FLAT legacy shape `{ ...productsKeyedById, date, store }`
 * (the Firestore dailyProductStats `*-productReport-*` docs). In Supabase mode
 * this calls the dedicated /reports/product-report endpoint, which aggregates
 * from source rows and therefore includes custom/delivery cart lines — the
 * movement-based getProductStatsForDays Supabase path returns a different
 * shape entirely and cannot carry pseudo lines.
 *
 * The Firestore branch reuses getProductStatsForDays' existing Firestore path
 * unchanged (array mode + generate-missing-days).
 */
export const getProductReportForDays = async (
  db: unknown,
  range: DateRange,
  store: string,
  generateForMissingDays = false
): Promise<Array<Record<string, unknown>>> => {
  if (shouldUseSupabase()) {
    return fetchProductReportDocsFromSupabase(range, store, 'productReport')
  }

  // Firestore mode: same docs the screen always used — shouldUseSupabase() is
  // false here, so getProductStatsForDays takes its Firestore path.
  return (await getProductStatsForDays(
    db,
    range,
    store,
    false,
    generateForMissingDays
  )) as unknown as Array<Record<string, unknown>>
}
